import * as React from 'react';

import { ReactNode } from 'react';

import { ScheduleOutlined, StarFilled, StarOutlined, TruckOutlined } from '@ant-design/icons';
import { Button, Flex, Image, Space, theme, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { useHover } from 'usehooks-ts';

import { NSKeys } from '@app/i18n';
import { CatalogStatus } from '@app/models/products';
import { getTimeUntilStart } from '@app/services/date';

import { ImageFault } from '@components/ImageFault';

import { CardStyled, Wrapper } from './styles';

interface IProps {
  onClick: () => void;
  title: string;
  warehouseName?: string;
  image?: string;
  disabled?: boolean;
  extra?: ReactNode;
  isPinned?: boolean;
  isPinLoading?: boolean;
  status?: CatalogStatus;
  startDate?: string | null;
  onPinClick?: React.MouseEventHandler<HTMLElement>;
}

export const Card: React.FC<IProps> = (props) => {
  const { token } = theme.useToken();
  const catalogsT = useTranslation(NSKeys.catalogs);
  const hoverRef = React.useRef(null);
  const isHover = useHover(hoverRef);
  const isScheduled = props.status === CatalogStatus.Scheduled;
  const timeUntilStart = getTimeUntilStart(props.startDate);
  const scheduledTooltipTime = timeUntilStart || catalogsT.t('list.scheduledCatalogTooltipUnknownTime');

  return (
    <Wrapper ref={hoverRef}>
      {isScheduled && (
        <Tooltip
          title={
            <span>
              {catalogsT.t('list.scheduledCatalogTooltip')}
              <br />
              {scheduledTooltipTime}
            </span>
          }>
          <ScheduleOutlined
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'absolute',
              top: 8,
              left: 10,
              zIndex: 18,
              color: token.colorPrimary,
              fontSize: 18,
            }}
          />
        </Tooltip>
      )}
      <CardStyled
        $ishover={isHover}
        hoverable={!props.disabled}
        onClick={() => !props.disabled && props.onClick()}
        actions={[]}
        $disabled={props.disabled}
        $isScheduled={isScheduled}
        extra={
          <Space onClick={(e) => e.stopPropagation()} size={4}>
            {props.extra}
          </Space>
        }
        cover={
          props.image ? (
            <Image
              height={155}
              width={250}
              style={{ objectFit: 'cover', borderRadius: 0 }}
              src={props.image}
              preview={false}
            />
          ) : (
            <Space>
              <ImageFault />
            </Space>
          )
        }>
        <Flex justify="space-between" gap={10}>
          <Flex align="center" gap={4} style={{ minWidth: 0 }}>
            <Typography.Title level={5} ellipsis={{ tooltip: props.title }} style={{ flex: 1, marginBottom: 0 }}>
              {props.title}
            </Typography.Title>
            {typeof props.isPinned === 'boolean' && props.onPinClick && (
              <Tooltip title={catalogsT.t(props.isPinned ? 'list.unpin' : 'list.pin')}>
                <Button
                  aria-label={catalogsT.t(props.isPinned ? 'list.unpin' : 'list.pin')}
                  type="text"
                  size="small"
                  loading={props.isPinLoading}
                  icon={props.isPinned ? <StarFilled style={{ color: token.colorWarning }} /> : <StarOutlined />}
                  onClick={props.onPinClick}
                  style={{ flexShrink: 0, opacity: props.isPinned || isHover ? 1 : 0 }}
                />
              </Tooltip>
            )}
          </Flex>
          {props.warehouseName && (
            <Tooltip title={`${catalogsT.t('form.warehouseLabel')}: ${props.warehouseName}`}>
              <TruckOutlined style={{ color: token.colorPrimary, fontSize: 18 }} />
            </Tooltip>
          )}
        </Flex>
      </CardStyled>
    </Wrapper>
  );
};
